import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { themeOptions } from '@pages/Profile/AccountSettings/SettingConsts'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useFeatureFlags } from '@context/FeatureFlagsContext'
import { useModal } from '@context/ModalsContext'
import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage, useSessionSettings } from '@context/SessionSettingsContext'

import { ListItem } from '@ui/components/List/ListItem/ListItem'
import { PageSection } from '@ui/components/PageSection'
import { BellIcon } from '@ui/icons/Bell.icon'
import { CalendarIcon } from '@ui/icons/Calendar.icon'
import { EuroIcon } from '@ui/icons/Euro.icon'
import { GlobeIcon } from '@ui/icons/Globe.icon'
import { LayoutIcon } from '@ui/icons/Layout.icon'
import { MailIcon } from '@ui/icons/Mail.icon'
import { MoonIcon } from '@ui/icons/Moon.icon'
import { SunIcon } from '@ui/icons/Sun.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { useNotificationFunctionality } from '@utils/notifications'
import { wait } from '@utils/wait'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { WalletCurrency } from '@model/WalletDto'

import { safeLocalStorage } from '@hooks/useStorage'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import { useThemeTracker } from '@analytics/amplitude/theme'

import { DocumentsPending, isDocumentPendingVisible } from '../DocumentManagement/DocumentsPending'
import { AccountSettingsBox } from './AccountSettingsBox'
import { ChangeEmailForm, ChangeEmailFormValues } from './ChangeEmailForm'
import {
  CommunicationLanguageForm,
  CommunicationLanguageFormValues,
} from './CommunicationLanguageForm'
import { DateFormatForm, DateFormatFormValues } from './DateFormatsForm'
import { DisplayCurrencyForm, DisplayCurrencyFormValues } from './DisplayCurrencyForm'
import { LandingPageForm, LandingPageFormValues } from './LandingPageForm'
import { NotificationPreferencesBlock } from './NotificationPreferences'

import styles from './AccountSettingsPage.module.scss'

enum LoadingKey {
  Newsletter,
  Language,
  DateFormat,
  Currency,
  LandingPage,
}

export const AccountSettingsPage: React.FC = () => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const [auth, setAuth] = useContext(AuthSessionContext)
  const locale = useSessionLanguage()
  const [settings, setSettings] = useSessionSettings()
  const { darkMode } = useFeatureFlags()
  const isMobile = useWindowResize()
  const { themeChanged } = useThemeTracker()

  const { refreshAccount } = useAccountWriteContext()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const { enableNotifications } = useNotificationFunctionality()
  const [componentLoading, setComponentLoading] = useState<LoadingKey[]>([])

  const currencyInfoModal = useModal(InformationModal, {
    title: t('Display Currency'),
    description: t('Choose a main currency to show an aggregated amount of your'),
  })

  const { data: languages = [] } = useQuery({
    queryKey: queryKeys.profile.clientAreaLanguages(),
    queryFn: () => apiClient.getClientAreaLanguages({ forSignIn: true }),
  })

  const filteredLanguages = languages.filter(
    (language) => language.clientAreaVisible || language.id === account?.communicationLanguage.id
  )

  const { data: landingPages = [], isLoading: isLandingPageLoading } = useQuery({
    queryKey: queryKeys.profile.landingPageOptions(locale),
    queryFn: () => apiClient.getLandingPageOptions(locale),
  })

  const { data: dateFormats = [] } = useQuery({
    queryKey: queryKeys.profile.dateFormats(),
    queryFn: () => apiClient.getClientDateFormats(),
  })

  const { data: currencies = [], isLoading: isCurrenciesLoading } = useQuery<WalletCurrency[]>({
    queryKey: queryKeys.profile.displayCurrencies(),
    queryFn: () => apiClient.getDisplayCurrencies(),
  })

  const loadingCall = async (
    calls: (() => Promise<void | AccountDetailedDto | undefined>)[],
    key: LoadingKey
  ) => {
    try {
      setComponentLoading((currentLoading) => [...currentLoading, key])
      for (const call of calls) {
        await call()
      }
    } finally {
      setComponentLoading((currentLoading) => currentLoading.filter((x) => x !== key))
    }
  }

  const handleSubmitNewsletter = async (values: ChangeEmailFormValues) => {
    await loadingCall(
      [
        () => apiClient.updateNewsletter(values.newsletterChoice),
        () => queryClient.invalidateQueries({ queryKey: ['account'] }),
        () => refreshAccount(locale),
      ],
      LoadingKey.Newsletter
    )
  }

  const handleSubmitCommunicationLanguage = async (values: CommunicationLanguageFormValues) => {
    if (account) {
      await loadingCall(
        [
          () => apiClient.updateCommunicationLanguage(account?.id, values),
          () => queryClient.invalidateQueries({ queryKey: ['account'] }),
          () => refreshAccount(locale),
        ],
        LoadingKey.Language
      )
    }
  }

  const handleSubmitDateFormat = async (values: DateFormatFormValues) => {
    if (account) {
      await loadingCall(
        [
          () => apiClient.updateClientDateFormat(account?.id, values),
          () => queryClient.invalidateQueries({ queryKey: ['account'] }),
        ],
        LoadingKey.DateFormat
      )
    }
    const newDateFormat = dateFormats.find(
      (dateFormat) => dateFormat.id === values.dateFormatTypeId
    )
    if (auth && newDateFormat) {
      const updatedAuth = {
        ...auth,
        dateFormatType: newDateFormat,
      }
      setAuth(updatedAuth)
      safeLocalStorage.setItem('auth', JSON.stringify(updatedAuth))
    }
  }

  const handleSubmitDisplayCurrency = async (values: DisplayCurrencyFormValues) => {
    await loadingCall(
      [
        () => apiClient.updateDisplayCurrency(values.displayCurrencyId),
        () => queryClient.invalidateQueries({ queryKey: ['account'] }),
        () => refreshAccount(locale),
      ],
      LoadingKey.Currency
    )
  }

  const handleSubmitLandingPage = async (values: LandingPageFormValues) => {
    if (values.landingPageId) {
      await loadingCall(
        [
          () => apiClient.updateLandingPage(values.landingPageId!),
          () => queryClient.invalidateQueries({ queryKey: ['account'] }),
          () => wait(),
          () => refreshAccount(locale),
        ],
        LoadingKey.LandingPage
      )
    }
  }

  return (
    <Loading isLoading={isCurrenciesLoading} showLoadingIcon>
      <Box flex direction='column' gap={isMobile ? 'x24' : 'x32'} fullWidth>
        <PageSection title={t('Profile.Preferences')} />
        {isDocumentPendingVisible(product, account) && <DocumentsPending />}

        <div className={styles.boxWrapper}>
          <AccountSettingsBox title={t('Profile.Landing Page')} icon={<LayoutIcon size={28} />}>
            <Loading
              asOverlay
              showLoadingIcon
              isLoading={isLandingPageLoading || componentLoading.includes(LoadingKey.LandingPage)}
            >
              <LandingPageForm
                landingPageOptions={landingPages}
                landingPage={account?.preferredLandingPage?.id}
                onSubmit={handleSubmitLandingPage}
              />
            </Loading>
          </AccountSettingsBox>

          <AccountSettingsBox
            title={t('Display Currency')}
            icon={<EuroIcon />}
            infoToggle={currencyInfoModal.open}
          >
            <Loading
              showLoadingIcon
              asOverlay
              isLoading={componentLoading.includes(LoadingKey.Currency)}
            >
              <DisplayCurrencyForm
                currencies={currencies}
                displayCurrency={account?.displayCurrency?.id}
                onSubmit={handleSubmitDisplayCurrency}
              />
            </Loading>
          </AccountSettingsBox>
          {darkMode && (
            <AccountSettingsBox
              title={t('Profile.Color Mode')}
              icon={
                settings.theme === 'light' ? (
                  <SunIcon color='text-high-em' size={28} />
                ) : (
                  <MoonIcon color='text-high-em' size={28} />
                )
              }
              isList
            >
              {themeOptions(t).map((x) => (
                <ListItem
                  key={x.name}
                  endSlot={{ radio: true }}
                  onClick={() => {
                    if (x.option !== settings.theme) {
                      themeChanged({
                        new_theme: x.option,
                        previous_theme: settings.theme,
                        theme_changed_source: 'preferences',
                      })
                    }
                    setSettings({ theme: x.option })
                  }}
                  active={settings.theme === x.option}
                  size='lg'
                >
                  {x.name}
                </ListItem>
              ))}
            </AccountSettingsBox>
          )}
          <AccountSettingsBox
            title={t('Profile.Newsletters')}
            icon={<MailIcon size={28} color='text-high-em' />}
          >
            <Loading
              showLoadingIcon
              asOverlay
              isLoading={componentLoading.includes(LoadingKey.Newsletter)}
            >
              <ChangeEmailForm
                newsletterChoice={account?.shouldReceiveNewsletter}
                onSubmit={handleSubmitNewsletter}
              />
            </Loading>
          </AccountSettingsBox>

          <AccountSettingsBox
            title={t('Profile.Communication Language')}
            icon={<GlobeIcon color='text-high-em' size={28} />}
          >
            <Loading
              showLoadingIcon
              asOverlay
              isLoading={componentLoading.includes(LoadingKey.Language)}
            >
              <CommunicationLanguageForm
                communicationLanguageId={account?.communicationLanguage.id}
                languages={filteredLanguages}
                onSubmit={handleSubmitCommunicationLanguage}
              />
            </Loading>
          </AccountSettingsBox>

          <AccountSettingsBox
            title={t('Profile.Date Format')}
            icon={<CalendarIcon color='text-high-em' size={28} />}
          >
            <Loading
              showLoadingIcon
              asOverlay
              isLoading={componentLoading.includes(LoadingKey.DateFormat)}
            >
              <DateFormatForm
                dateFormatTypeId={auth?.dateFormatType?.id}
                dateFormats={dateFormats}
                onSubmit={handleSubmitDateFormat}
              />
            </Loading>
          </AccountSettingsBox>

          {enableNotifications && (
            <AccountSettingsBox
              title={t('Notifications.Notifications')}
              icon={<BellIcon color='text-high-em' size={28} />}
            >
              <NotificationPreferencesBlock />
            </AccountSettingsBox>
          )}
        </div>
      </Box>
    </Loading>
  )
}
